import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import * as api from "../api/client";

const when = (s) => (s ? new Date(s * 1000).toLocaleString() : "—");
const dur = (a, b) => {
  if (!a) return "—";
  const s = Math.max(0, (b || Date.now() / 1000) - a);
  return s >= 60 ? `${Math.floor(s / 60)}m ${Math.round(s % 60)}s` : `${s.toFixed(1)}s`;
};
const SCHEDULE = { off: "Manual only", hourly: "Every hour", daily: "Every day", weekly: "Every week" };
const STATUS = { ok: "ok", failed: "failed", running: "running", cancelled: "", skipped: "", pending: "" };

function RunStatus({ s }) {
  return <span className={`cx-status ${STATUS[s] ?? ""}`}>{s === "ok" ? "Succeeded" : s}</span>;
}

export default function JobsPage() {
  const [jobs, setJobs] = useState(null);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setJobs((await api.listJobs()).jobs);
    } catch (e) {
      setErr(e.message);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const running = (jobs || []).some((j) => j.running);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [running, load]);

  async function runNow(e, j) {
    e.stopPropagation();
    try {
      await api.runJob(j.id);
      await load();
    } catch (er) {
      setErr(er.message);
    }
  }

  return (
    <div className="cx-page">
      <header className="cx-head">
        <div>
          <h1 className="cx-title">Jobs</h1>
          <p className="cx-sub">Pipelines that keep QLens current: refresh imported data, retrain models and rewrite their predictions, in order, on a schedule. Each step retries on failure, and every run is recorded step by step.</p>
        </div>
        <button type="button" className="cx-btn primary" onClick={() => setEditing({})}>+ Create job</button>
      </header>
      {err && <div className="cx-msgbox bad">{err}</div>}
      {!jobs ? <div className="cx-card cx-loading"><Spinner animation="border" size="sm" /></div> : jobs.length === 0 ? (
        <div className="cx-card cx-empty">
          <strong>No jobs yet</strong>
          <span>Example: every night, refresh the sales import, retrain the sales forecast, write the new forecast table.</span>
          <button type="button" className="cx-btn primary" onClick={() => setEditing({})}>Create your first job</button>
        </div>
      ) : (
        <section className="cx-card">
          <div className="cx-scroll ml-list">
            <table className="cx-table">
              <thead><tr><th>Job</th><th>Pipeline</th><th>Schedule</th><th>Last run</th><th>Next run</th><th /></tr></thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="ml-row" onClick={() => navigate(`/jobs/${j.id}`)}>
                    <td className="cx-strong"><Link to={`/jobs/${j.id}`}>{j.name}</Link></td>
                    <td><Pipeline tasks={j.tasks} compact /></td>
                    <td>{SCHEDULE[j.schedule]}</td>
                    <td>{j.running ? <RunStatus s="running" /> : j.lastRun ? <><RunStatus s={j.lastRun.status} /><div className="cx-muted">{when(j.lastRun.startedAt)}</div></> : <span className="cx-muted">Never</span>}</td>
                    <td className="cx-muted">{j.schedule === "off" ? "—" : when(j.nextRunAt)}</td>
                    <td><button type="button" className="cx-btn" disabled={j.running} onClick={(e) => runNow(e, j)}>Run now</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      {editing && <JobEditor job={editing} onClose={() => setEditing(null)} onSaved={(j) => { setEditing(null); navigate(`/jobs/${j.id}`); }} />}
    </div>
  );
}

function Pipeline({ tasks, compact, steps }) {
  return (
    <div className={"jb-pipe" + (compact ? " compact" : "")}>
      {tasks.map((t, i) => {
        const s = steps?.[i];
        return (
          <div key={t.key} className="jb-node-wrap">
            {i > 0 && <span className="jb-arrow" aria-hidden="true">→</span>}
            <div className={"jb-node " + (s ? s.status : "")} title={t.label}>
              {!compact && <span className="jb-node-n">{i + 1}</span>}
              <span className="jb-node-t">{compact ? t.typeLabel : t.label}</span>
              {!compact && <small>{t.typeLabel}{t.retries ? ` · ${t.retries} retr${t.retries > 1 ? "ies" : "y"}` : ""}</small>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function JobDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [sel, setSel] = useState(null);
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      setJob(await api.getJob(id));
    } catch (e) {
      setErr(e.message);
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  const live = job && (job.running || job.runs[0]?.status === "running");
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(load, 1500);
    return () => clearInterval(t);
  }, [live, load]);

  async function act(fn, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return null;
    setErr("");
    try {
      const r = await fn();
      await load();
      return r;
    } catch (e) {
      setErr(e.message);
      return null;
    }
  }

  if (!job) return <div className="cx-page">{err ? <div className="cx-msgbox bad">{err}</div> : <div className="cx-loading"><Spinner animation="border" size="sm" /></div>}</div>;
  const run = job.runs.find((r) => r.id === sel) || job.runs[0];

  return (
    <div className="cx-page">
      <Link to="/jobs" className="ml-back">← Jobs</Link>
      <header className="cx-head">
        <div>
          <h1 className="cx-title">{job.name}</h1>
          <p className="cx-sub">{SCHEDULE[job.schedule]}{job.schedule !== "off" && ` · next run ${when(job.nextRunAt)}`} · created by {job.createdBy || "—"}</p>
        </div>
        <div className="ml-actions">
          {live
            ? <button type="button" className="cx-btn danger" onClick={() => act(() => api.cancelJob(job.id))}>Cancel run</button>
            : <button type="button" className="cx-btn primary" onClick={() => act(() => api.runJob(job.id)).then(() => setSel(null))}>Run now</button>}
          <button type="button" className="cx-btn" disabled={live} onClick={() => setEditing(true)}>Edit</button>
          <button type="button" className="cx-btn danger" disabled={live}
                  onClick={async () => (await act(() => api.deleteJob(job.id), `Delete the job “${job.name}” and its run history?`)) && navigate("/jobs")}>Delete</button>
        </div>
      </header>
      {err && <div className="cx-msgbox bad">{err}</div>}

      <section className="cx-card jb-graph">
        <div className="cx-card-h">Pipeline{run ? ` · ${run.trigger} run ${when(run.startedAt)}` : ""}</div>
        <Pipeline tasks={job.tasks} steps={run?.tasks} />
        <p className="cx-muted cx-note">Steps run in order. If a step still fails after its retries, the steps after it are skipped.</p>
      </section>

      {run && (
        <section className="cx-card ml-body">
          <h3 className="ml-h">Run details <RunStatus s={run.status} /></h3>
          <div className="cx-scroll">
            <table className="cx-table">
              <thead><tr><th>#</th><th>Step</th><th>Status</th><th>Attempts</th><th>Duration</th><th>Result</th></tr></thead>
              <tbody>
                {run.tasks.map((s, i) => (
                  <tr key={s.key}>
                    <td>{i + 1}</td>
                    <td className="cx-strong">{s.label}</td>
                    <td><RunStatus s={s.status} /></td>
                    <td>{s.attempts || "—"}</td>
                    <td>{s.startedAt ? dur(s.startedAt, s.finishedAt) : "—"}</td>
                    <td className="jb-result">{s.error ? <span className="cx-err">{s.error}</span> : s.detail || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="cx-card ml-body">
        <h3 className="ml-h">Run history</h3>
        {job.runs.length === 0 ? <p className="cx-muted">Not run yet.</p> : (
          <div className="cx-scroll">
            <table className="cx-table">
              <thead><tr><th>Started</th><th>Trigger</th><th>By</th><th>Duration</th><th>Status</th></tr></thead>
              <tbody>
                {job.runs.map((r) => (
                  <tr key={r.id} className={"ml-row" + (r.id === run?.id ? " ml-best" : "")} onClick={() => setSel(r.id)}>
                    <td>{when(r.startedAt)}</td><td>{r.trigger}</td><td>{r.startedBy}</td>
                    <td>{dur(r.startedAt, r.finishedAt)}</td><td><RunStatus s={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {editing && <JobEditor job={job} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
    </div>
  );
}

function JobEditor({ job, onClose, onSaved }) {
  const [opts, setOpts] = useState(null);
  const [name, setName] = useState(job.name || "");
  const [schedule, setSchedule] = useState(job.schedule || "off");
  const [tasks, setTasks] = useState(() => (job.tasks || []).map((t) => ({ type: t.type, ref: t.ref, retries: t.retries })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.jobOptions().then(setOpts).catch((e) => setErr(e.message));
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const upd = (i, patch) => setTasks((ts) => ts.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const move = (i, d) => setTasks((ts) => {
    const out = [...ts];
    [out[i], out[i + d]] = [out[i + d], out[i]];
    return out;
  });
  const refs = (type) => (type === "import_refresh" ? opts?.imports : type === "refresh_agents" ? null : opts?.models) || [];

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const body = { name, schedule, tasks };
      onSaved(job.id ? await api.updateJob(job.id, body) : await api.createJob(body));
    } catch (er) {
      setErr(er.message);
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="cx-modal ml-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Job">
        <div className="cx-modal-h">
          <div><div className="cx-modal-title">{job.id ? "Edit job" : "Create job"}</div><div className="cx-muted">Steps run top to bottom</div></div>
          <button type="button" className="cx-x-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        {!opts ? <div className="cx-loading"><Spinner animation="border" size="sm" /></div> : (
          <form onSubmit={save} className="cx-form-wrap">
            <div className="cx-modal-body">
              <div className="cx-grid">
                <label className="cx-field"><span>Name</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required placeholder="e.g. Nightly sales refresh" /></label>
                <label className="cx-field"><span>Schedule</span>
                  <select value={schedule} onChange={(e) => setSchedule(e.target.value)}>
                    {Object.entries(SCHEDULE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select></label>
              </div>
              <div className="jb-tasks">
                {tasks.map((t, i) => (
                  <div key={i} className="jb-task">
                    <span className="jb-node-n">{i + 1}</span>
                    <select value={t.type} onChange={(e) => upd(i, { type: e.target.value, ref: "" })}>
                      {opts.types.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                    </select>
                    {t.type !== "refresh_agents" ? (
                      <select value={t.ref} onChange={(e) => upd(i, { ref: e.target.value })} required>
                        <option value="">{t.type === "import_refresh" ? "Choose an import…" : "Choose a model…"}</option>
                        {refs(t.type).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                      </select>
                    ) : <span className="cx-muted jb-task-fill">Re-reads tables so Chat sees new or changed data</span>}
                    <label className="jb-retry">retries
                      <input type="number" min={0} max={5} value={t.retries} onChange={(e) => upd(i, { retries: Number(e.target.value) })} />
                    </label>
                    <span className="jb-task-btns">
                      <button type="button" className="cx-link" disabled={i === 0} onClick={() => move(i, -1)} title="Move up">↑</button>
                      <button type="button" className="cx-link" disabled={i === tasks.length - 1} onClick={() => move(i, 1)} title="Move down">↓</button>
                      <button type="button" className="cx-link danger" onClick={() => setTasks((ts) => ts.filter((_, j) => j !== i))} title="Remove">✕</button>
                    </span>
                  </div>
                ))}
                <button type="button" className="cx-btn" disabled={tasks.length >= 20}
                        onClick={() => setTasks((ts) => [...ts, { type: "model_retrain", ref: "", retries: 1 }])}>+ Add step</button>
                {opts.models.length === 0 && opts.imports.length === 0 && (
                  <p className="cx-muted">Tip: create a model (Models) or import a table (Connections) to use them as steps.</p>
                )}
              </div>
              {err && <div className="cx-msgbox bad">{err}</div>}
            </div>
            <div className="cx-modal-f">
              <button type="submit" className="cx-btn primary" disabled={busy || !name.trim() || tasks.length === 0}>
                {busy ? <Spinner size="sm" animation="border" /> : "Save job"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
